// Product Analyzer data layer — pure helpers + fixture-derived adapters (Walmart terminology).
// NOTE: All Walmart values are FIXTURE-derived (no approved Walmart API yet). Never labeled live.

export const ANALYZER_TABS = ["overview", "history", "sellers", "data", "variations", "details"];

// Legacy "demand" state safely redirects to "data".
export function normalizeAnalyzerTab(value) {
  if (value === "demand") return "data";
  return ANALYZER_TABS.includes(value) ? value : "overview";
}

// Data area = unified Offers workspace (Offers / Buy Box / Used Buy Box) + fee & health utilities.
// Legacy "featured-offer" and "pricing" fold into "offers".
export const WALMART_DATA_SECTIONS = [
  { id: "offers", label: "Offers" },
  { id: "buybox", label: "Buy Box Statistics" },
  { id: "used-buybox", label: "Used Buy Box Statistics" },
  { id: "wfs-fees", label: "WFS & Fees" },
  { id: "listing-health", label: "Listing Health" },
];
const VALID_DATA_SECTIONS = new Set(WALMART_DATA_SECTIONS.map((s) => s.id));
export function normalizeWalmartDataSection(value) {
  if (!value || value === "pricing" || value === "featured-offer") return "offers";
  return VALID_DATA_SECTIONS.has(value) ? value : "offers";
}

// Selected chart period -> highlighted Historical Comparison column (null = no single column).
export function comparisonColumnForPeriod(period) {
  return { "30d": "average30d", "90d": "average90d", "180d": "average180d", "1y": "average365d" }[period] || null;
}

// ---- External research URL builders (use the RAW full product title) ----
/** Google Shopping results for the product title (`udm=28`). */
export function buildGoogleSearchUrl(title) {
  const q = (title || "").trim();
  if (!q) return null;
  return `https://www.google.com/search?udm=28&q=${encodeURIComponent(q)}`;
}
export function buildWalmartSearchUrl(title) {
  const q = (title || "").trim();
  if (!q) return null;
  return `https://www.walmart.com/search?q=${encodeURIComponent(q)}`;
}
/** Amazon title search — shared Analyzer / SFH builder. */
export function buildAmazonSearchUrl(title) {
  const q = (title || "").trim();
  if (!q) return null;
  return `https://www.amazon.com/s?k=${encodeURIComponent(q)}`;
}
/** Rakuten catalog search for the product title. */
export function buildRakutenSearchUrl(title) {
  const q = (title || "").trim();
  if (!q) return null;
  return `https://www.rakuten.com/search?term=${encodeURIComponent(q)}&type=suggest`;
}
export function buildAmazonProductUrl(asin) { return asin ? `https://www.amazon.com/dp/${encodeURIComponent(String(asin).trim())}` : null; }
export function buildWalmartProductUrl(itemId) {
  const digits = String(itemId || "").replace(/\D/g, "");
  return digits ? `https://www.walmart.com/ip/${digits}` : null;
}

// Match confidence status metadata (never color-only).
export const MATCH_STATUS = {
  exact: { tone: "success", label: "Exact" },
  probable: { tone: "success", label: "Probable" },
  possible: { tone: "warning", label: "Possible" },
  "needs-review": { tone: "warning", label: "Needs review" },
  needs_review: { tone: "warning", label: "Needs review" },
  "no-match": { tone: "danger", label: "No match" },
  no_match: { tone: "danger", label: "No match" },
};

const clamp = (n) => Math.max(0, Math.min(100, Math.round(n)));

// ---- Profitability (strict: any missing required input => null result) ----
export function isValidNumber(v) { return typeof v === "number" && Number.isFinite(v); }
export function average(values) { const v = (values || []).filter(isValidNumber); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; }
export function minimum(values) { const v = (values || []).filter(isValidNumber); return v.length ? Math.min(...v) : null; }
export function maximum(values) { const v = (values || []).filter(isValidNumber); return v.length ? Math.max(...v) : null; }

export function calculateProfitability(state) {
  const required = [state.salePrice, state.costOfGoods, state.inboundShipping, state.prepAndLabeling,
    state.referralFee, state.wfsFulfillmentFee, state.storageFee, state.returnAllowance, state.additionalCosts];
  if (!required.every(isValidNumber)) return { totalCosts: null, netProfit: null, margin: null, roi: null, revenue: null, breakEvenPrice: null };
  const totalCosts = state.costOfGoods + state.inboundShipping + state.prepAndLabeling + state.referralFee
    + state.wfsFulfillmentFee + state.storageFee + state.returnAllowance + state.additionalCosts;
  const netProfit = state.salePrice - totalCosts;
  const margin = state.salePrice > 0 ? (netProfit / state.salePrice) * 100 : null;
  const acquisition = state.costOfGoods + state.inboundShipping + state.prepAndLabeling;
  const roi = acquisition > 0 ? (netProfit / acquisition) * 100 : null;
  return { totalCosts, netProfit, margin, roi, revenue: state.salePrice, breakEvenPrice: totalCosts };
}

// ---- Opportunity score (transparent weighted, skips missing factors) ----
const SCORE_WEIGHTS = {
  profitabilityScore: 0.3, demandScore: 0.2, competitionScore: 0.15, stabilityScore: 0.15,
  featuredOfferScore: 0.1, matchConfidence: 0.05, freshnessScore: 0.05,
};

export function calculateOpportunityScore(inputs) {
  const available = Object.entries(SCORE_WEIGHTS)
    .map(([key, weight]) => ({ value: inputs[key], weight }))
    .filter((i) => typeof i.value === "number" && Number.isFinite(i.value));
  if (available.length < 4) return null;
  const availableWeight = available.reduce((t, i) => t + i.weight, 0);
  const weighted = available.reduce((t, i) => t + i.value * i.weight, 0);
  return Math.round(weighted / availableWeight);
}

export function opportunityLabel(score) {
  if (score === null || score === undefined) return "unknown";
  if (score >= 82) return "best-opportunity";
  if (score >= 68) return "strong";
  if (score >= 50) return "review";
  return "avoid";
}

export const OPP_STYLE = {
  "best-opportunity": { tone: "success", label: "Best opportunity" },
  strong: { tone: "success", label: "Strong" },
  review: { tone: "warning", label: "Review" },
  avoid: { tone: "danger", label: "Avoid" },
  unknown: { tone: "neutral", label: "Unknown" },
};

const DEMAND_SCORE = { High: 88, Moderate: 60, Low: 34 };
const DEMAND_UNITS = { High: 1200, Moderate: 640, Low: 210 };

// Enrich the flat fixture VARIATIONS into the comparison model + opportunity scores.
export function adaptVariations(rawVariations = []) {
  const rows = rawVariations.map((v, i) => {
    const profitabilityScore = v.roi == null ? null : clamp(v.roi * 1.7);
    const demandScore = DEMAND_SCORE[v.demand] ?? null;
    const competitionScore = v.sellers == null ? null : clamp(100 - v.sellers * 9);
    const stabilityScore = v.confidence == null ? null : clamp(v.confidence * 0.85 + 12);
    const featuredOfferScore = v.sellers == null ? null : clamp(92 - v.sellers * 6);
    const matchConfidence = v.confidence ?? null;
    const freshnessScore = 82;
    const score = calculateOpportunityScore({
      profitabilityScore, demandScore, competitionScore, stabilityScore, featuredOfferScore, matchConfidence, freshnessScore,
    });
    const reasons = [];
    if (v.roi != null && v.roi >= 45) reasons.push("Highest profit among tracked variations");
    else if (v.roi != null && v.roi >= 28) reasons.push("Meets 30% ROI target");
    if (v.demand === "High") reasons.push("Strong 30-day demand");
    else if (v.demand === "Low") reasons.push("Weak demand signal");
    if (v.sellers != null && v.sellers <= 3) reasons.push("Low seller count");
    if (v.sellers != null && v.sellers >= 7) reasons.push("Crowded — many competing sellers");
    if (v.confidence != null && v.confidence < 70) reasons.push("Low match confidence");
    if (score == null) reasons.push("Insufficient data to score");

    const price = v.price ?? null;
    const walmartRetailPresent = i === 0; // fixture: 1P present on primary variant
    return {
      id: v.item_id || `var_${i}`,
      walmartItemId: v.item_id,
      upc: v.upc,
      title: v.variant,
      attributes: { Pack: `${v.pack} ct`, Size: (v.variant || "").split("·")[0]?.trim() || "—" },
      packCount: v.pack,
      unitSize: (v.variant || "").split("·")[0]?.trim() || null,
      pricing: {
        featuredOffer: price,
        unitPrice: price != null && v.pack ? +(price / v.pack).toFixed(2) : null,
        walmartRetail: price,
        lowestMarketplace: price != null ? +(price * 0.98).toFixed(2) : null,
        average90d: price != null ? +(price * 1.01).toFixed(2) : null,
      },
      competition: {
        featuredSeller: i === 0 ? "Walmart" : "Marketplace seller",
        featuredFulfillment: i === 0 ? "walmart" : v.sellers > 2 ? "wfs" : "seller",
        wfsEligible: true,
        sellerCount: v.sellers,
        walmartRetailPresent,
      },
      demand: {
        estimated30DayUnits: DEMAND_UNITS[v.demand] ?? null,
        label: v.demand,
        stockStatus: "in-stock",
      },
      reviews: { rating: v.rating, count: v.reviews },
      reviewCount: v.reviews ?? null,
      profitability: {
        netProfit: v.profit,
        roi: v.roi,
        margin: price ? +((v.profit / price) * 100).toFixed(1) : null,
        maximumAcquisitionCost: price != null ? +(price * 0.42).toFixed(2) : null,
      },
      matchConfidence,
      trackingSince: "2026-01-06",
      scores: { profitabilityScore, demandScore, competitionScore, stabilityScore, featuredOfferScore, matchConfidence, freshnessScore },
      opportunityScore: score,
      reasons,
      opportunity: opportunityLabel(score),
    };
  });

  // Exactly one primary "Best opportunity" marker — the single highest scored row.
  let bestIdx = -1, bestScore = -1;
  rows.forEach((r, i) => { if (r.opportunityScore != null && r.opportunityScore > bestScore) { bestScore = r.opportunityScore; bestIdx = i; } });
  rows.forEach((r, i) => {
    if (r.opportunityScore == null) r.opportunity = "unknown";
    else if (i === bestIdx) r.opportunity = "best-opportunity";
    else r.opportunity = r.opportunityScore >= 68 ? "strong" : r.opportunityScore >= 50 ? "review" : "avoid";
    r.isBest = i === bestIdx;
  });
  return rows;
}

// Group flat product_details into Product Details tab sections.
export function groupProductDetails(details = []) {
  const has = (label, ...keys) => keys.some((k) => label.toLowerCase().includes(k));
  const groups = {
    identity: [], catalog: [], packaging: [], fulfillment: [], tracking: [],
  };
  details.forEach((d) => {
    const l = d.label;
    if (has(l, "item id", "upc", "barcode", "brand", "model", "gtin", "mpn", "sku")) groups.identity.push(d);
    else if (has(l, "category", "rating", "title", "color", "material")) groups.catalog.push(d);
    else if (has(l, "pack", "unit", "dimension", "weight")) groups.packaging.push(d);
    else if (has(l, "size tier", "wfs", "hazmat", "oversize", "restricted", "buy box", "seller")) groups.fulfillment.push(d);
    else if (has(l, "tracking", "since")) groups.tracking.push(d);
    else groups.catalog.push(d);
  });
  return groups;
}

// Per-seller Featured Offer ownership by period. Seller shares reconcile with fulfillment
// totals; missing observation time is surfaced as "suppressed", never redistributed.
const OWN_FULFILLMENT = {
  "Walmart Retail": { key: "walmart", label: "Walmart.com" },
  WFS: { key: "wfs", label: "WFS" },
  "Seller Fulfilled": { key: "seller", label: "Seller fulfilled" },
};
const OWN_PERIOD_DAYS = { "30D": 30, "90D": 90, "180D": 180, "1Y": 365 };
// Deterministic fixture observation shares per period (index-aligned to fixtures.OFFERS).
const OWN_SHARES = {
  "30D": { shares: [55, 20, 12, 6, 4], suppressed: 3 },
  "90D": { shares: [62, 17, 11, 7, 3], suppressed: 0 },
  "180D": { shares: [65, 15, 10, 6, 2], suppressed: 2 },
  "1Y": { shares: [68, 14, 9, 5, 2], suppressed: 2 },
};
const OWN_ROTATION = [24, 8, 6, 3, 2];

export function buildFeaturedOwnership(offers = [], period = "90D") {
  const days = OWN_PERIOD_DAYS[period] || 90;
  const cfg = OWN_SHARES[period] || OWN_SHARES["90D"];
  const totals = { walmart: 0, wfs: 0, seller: 0, suppressed: cfg.suppressed };
  const sellers = offers.slice(0, cfg.shares.length).map((o, i) => {
    const share = cfg.shares[i] ?? 0;
    const f = OWN_FULFILLMENT[o.fulfillment] || { key: "seller", label: o.fulfillment };
    totals[f.key] += share;
    return {
      seller: o.seller,
      featured: !!o.featured,
      fulfillmentKey: f.key,
      fulfillmentLabel: f.label,
      share,
      ownershipDays: Math.round((share / 100) * days),
      rotation: Math.max(1, Math.round(((OWN_ROTATION[i] ?? 2) * days) / 90)),
      avgLanded: +(o.delivered * (0.99 + i * 0.006)).toFixed(2),
      currentLanded: o.delivered,
      streak: o.featured ? "42h" : "—",
      lastObserved: o.last_observed,
    };
  });
  return { period, days, sellers, suppressed: cfg.suppressed, totals };
}


// ============================================================================
// UNIFIED OFFERS WORKSPACE — normalized model + fixture adapters (Walmart terms)
// All values FIXTURE-derived; per-seller history is deterministic + labeled estimated.
// Architecture lets a real capture feed / API replace the fixtures without UI rework.
// ============================================================================

export const OFFERS_SUBSECTIONS = [
  { id: "offers", label: "Offers" },
  { id: "buybox", label: "Buy Box Statistics" },
  { id: "used-buybox", label: "Used Buy Box Statistics" },
];

export const OFFER_PERIODS = ["24H", "7D", "30D", "90D", "180D", "1Y", "2Y", "5Y", "All"];
const PERIOD_DAYS = { "24H": 1, "7D": 7, "30D": 30, "90D": 90, "180D": 180, "1Y": 365, "2Y": 730, "5Y": 1825, All: 1825 };
export const periodToDays = (p) => PERIOD_DAYS[p] ?? 90;
export const MAX_HISTORY_DAYS = 1825; // full available synthetic dataset ("All")

export const SELLER_COLORS = ["#059669", "#2563EB", "#F97316", "#8B5CF6", "#DB2777", "#0891B2", "#CA8A04"];

// All 16 required columns (id, label, group, tooltip). Order = default order.
export const OFFER_COLUMNS = [
  { id: "seller", label: "Seller", always: true, pinnable: true },
  { id: "price", label: "Price + Shipping Costs" },
  { id: "condition", label: "Condition" },
  { id: "priceHistory", label: "Price History" },
  { id: "stock", label: "Stock" },
  { id: "sold", label: "Sold" },
  { id: "sold30", label: "Sold in the Last 30 Days" },
  { id: "stockHistory", label: "Stock History" },
  { id: "walmartPlus", label: "Walmart+" },
  { id: "sra", label: "SRA", tooltip: "Sales Rank Average — average sales rank over the period (external Keepa reference). Not provided by the current Walmart data source." },
  { id: "isWalmart", label: "Is Walmart" },
  { id: "rating12", label: "Rating (Past 12 Months)" },
  { id: "reviewsLifetime", label: "Review Count (Lifetime)" },
  { id: "coupon", label: "Coupon" },
  { id: "current", label: "Current" },
  { id: "firstSeen", label: "First Seen" },
];

export const OFFER_PRESETS = {
  Overview: ["seller", "price", "condition", "priceHistory", "stock", "current"],
  Pricing: ["seller", "price", "priceHistory", "coupon", "current"],
  History: ["seller", "price", "priceHistory", "stockHistory", "firstSeen", "current"],
  "Stock and Sales": ["seller", "stock", "sold", "sold30", "stockHistory", "current"],
  Fulfillment: ["seller", "price", "walmartPlus", "isWalmart", "condition", "current"],
  "Seller Quality": ["seller", "rating12", "reviewsLifetime", "sra", "isWalmart", "current"],
  "All Columns": OFFER_COLUMNS.map((c) => c.id),
};

function seedFrom(str) { let h = 2166136261; for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
function rngFrom(seed) { let s = seed >>> 0 || 1; return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296; }; }

const FULFILLMENT_TYPE = {
  "Walmart Retail": "WALMART_RETAIL",
  WFS: "WFS",
  "Seller Fulfilled": "SELLER_FULFILLED",
};
export const FULFILLMENT_LABEL = { WALMART_RETAIL: "Walmart", WFS: "WFS", SELLER_FULFILLED: "Seller Fulfilled", UNKNOWN: "Unknown" };

// Normalize a raw fixture offer into an OfferRecord. Unknown source values stay `undefined`.
export function adaptOffers(rawOffers = []) {
  return rawOffers.map((o, i) => {
    const seed = seedFrom(`${o.seller}|${i}`);
    const r = rngFrom(seed);
    const fulfillmentType = FULFILLMENT_TYPE[o.fulfillment] || "UNKNOWN";
    const isWfs = fulfillmentType === "WFS";
    const isSellerFulfilled = fulfillmentType === "SELLER_FULFILLED";
    // Walmart Retail identity ONLY from the explicit source fulfillment flag, never the seller name.
    const isWalmartRetail = fulfillmentType === "WALMART_RETAIL" ? true : (fulfillmentType === "UNKNOWN" ? undefined : false);
    const itemPrice = isValidNumber(o.item_price) ? o.item_price : undefined;
    const shippingPrice = isValidNumber(o.shipping) ? o.shipping : undefined; // 0 = explicitly free
    const landedPrice = (isValidNumber(itemPrice) && isValidNumber(shippingPrice))
      ? +(itemPrice + shippingPrice).toFixed(2)
      : (isValidNumber(o.delivered) ? o.delivered : undefined);
    const hasFreeShipping = shippingPrice === 0 ? true : (isValidNumber(shippingPrice) ? false : undefined);
    // Seller-fulfilled stock is sometimes unavailable from the source.
    const stockKnown = !(isSellerFulfilled && r() < 0.4);
    const estimatedStock = stockKnown ? Math.round(18 + r() * 380) : undefined;
    return {
      offerId: `offer_${i + 1}`,
      sellerId: `WMS-${100000 + (seed % 899999)}`,
      sellerName: o.seller,
      sellerStorefrontUrl: `https://www.walmart.com/seller/${100000 + (seed % 899999)}`,
      fulfillmentType, isWfs, isSellerFulfilled, isWalmartRetail,
      isWalmartPlusEligible: undefined,           // not provided by current Walmart source
      hasFreeShipping,
      hasTwoDayDelivery: isWfs ? true : undefined,
      hasExpeditedDelivery: undefined,
      shipsFrom: undefined, soldBy: o.seller,
      itemPrice, shippingPrice, landedPrice, currency: "USD",
      condition: "New", subcondition: undefined, conditionComment: undefined,
      stock: estimatedStock, estimatedStock, soldIsEstimated: true,
      sold: Math.round(220 + r() * 1600),
      sold30Days: Math.round(18 + r() * 190),
      sra: undefined,                              // Sales Rank Average — not a Walmart source field
      sellerRating12Months: isValidNumber(o.rating) ? o.rating : undefined,
      sellerLifetimeReviewCount: Math.round(400 + r() * 9000),
      couponType: undefined, couponValue: undefined, couponEligibility: undefined, couponExpiresAt: undefined,
      isCurrent: true, isHistorical: false,
      isOutOfStock: estimatedStock === 0, isSuppressed: false,
      isBuyBox: !!o.featured, buyBoxEligible: isSellerFulfilled ? undefined : true,
      firstSeenAt: new Date(Date.now() - (28 + i * 21) * 864e5).toISOString(),
      lastSeenAt: o.last_observed, lastCheckedAt: o.last_observed,
      _roi: o.roi, _estProfit: o.est_profit, _rawFulfillment: o.fulfillment,
    };
  });
}

// Deterministic per-seller observation series (OfferHistoryPoint[]). Gaps kept as `undefined`.
export function buildOfferHistory(offer, days = 90) {
  const r = rngFrom(seedFrom(`${offer.sellerId}|${days}`));
  const step = days <= 1 ? 1 : days <= 7 ? 1 : days <= 30 ? 2 : days <= 90 ? 3 : days <= 365 ? 7 : days <= 730 ? 14 : 30;
  const pts = [];
  let factor = 1;                                   // stepped price factor — holds until an actual change
  let stock = offer.estimatedStock;
  const gapAt = Math.floor(days * 0.5);             // one guaranteed missing observation (visible gap)
  for (let d = days; d >= 0; d -= step) {
    const t = new Date(Date.now() - d * 864e5).toISOString();
    const gap = r() < 0.05 || (d <= gapAt && d > gapAt - step);
    if (r() < 0.4) factor = +(factor * (1 + (r() - 0.5) * 0.16)).toFixed(4); // occasional step, else hold
    const item = isValidNumber(offer.itemPrice) ? +(offer.itemPrice * factor).toFixed(2) : undefined;
    const ship = offer.shippingPrice;
    const landed = (isValidNumber(item) && isValidNumber(ship)) ? +(item + ship).toFixed(2)
      : (isValidNumber(offer.landedPrice) ? +(offer.landedPrice * factor).toFixed(2) : undefined);
    if (isValidNumber(stock) && r() < 0.45) stock = Math.max(0, Math.round(stock + (r() - 0.48) * 130)); // stepped; real zeros possible
    pts.push({
      timestamp: t, sellerId: offer.sellerId, offerId: offer.offerId,
      itemPrice: gap ? undefined : item,
      shippingPrice: gap ? undefined : ship,
      landedPrice: gap ? undefined : landed,
      currency: offer.currency,
      stock: gap || !isValidNumber(stock) ? undefined : stock,
      stockIsEstimated: true,
      isCurrent: d === 0,
      isBuyBox: offer.isBuyBox && d < days * 0.45,
      condition: "New",
      fulfillmentType: offer.fulfillmentType,
    });
  }
  return pts;
}

// Merge per-seller series into a recharts-friendly matrix keyed by sellerId. Timestamps align
// because every seller uses the same day/step loop.
export function buildOfferSeriesMatrix(offers, days, valueKey) {
  const perSeller = offers.map((o) => buildOfferHistory(o, days));
  const len = perSeller[0]?.length || 0;
  const points = [];
  for (let i = 0; i < len; i++) {
    const row = { t: perSeller[0][i].timestamp };
    offers.forEach((o, s) => { row[o.sellerId] = perSeller[s]?.[i]?.[valueKey]; });
    points.push(row);
  }
  return points;
}

// Single per-seller {t, v} series for in-row mini-charts. Uses the seller's OWN deterministic
// observations (seeded by sellerId) — never a generic shared series. Missing = undefined (real gap).
export function buildOfferSeries(offer, days, valueKey) {
  return buildOfferHistory(offer, days).map((p) => ({ t: p.timestamp, v: p[valueKey] ?? null }));
}

// Deterministic per-variation review-count observations (VariationReviewHistoryPoint[]).
// Reviews accrue monotonically toward the current reviewCount. Seeded by the variation id so
// each variation has its OWN series (never a shared generic line). Gaps preserved (null).
// `endDaysAgo` lets a window end in the past (supports prev/next period + custom end date).
export function buildReviewHistory(variation, days = 365, endDaysAgo = 0) {
  const total = variation?.reviewCount;
  if (total == null || !Number.isFinite(total)) return []; // no history -> caller shows "No review history available"
  const r = rngFrom(seedFrom(`rev|${variation.id}`));
  const step = days <= 1 ? 1 : days <= 7 ? 1 : days <= 30 ? 2 : days <= 90 ? 3 : days <= 365 ? 10 : days <= 730 ? 20 : 45;
  const startD = days + endDaysAgo;
  const idxs = [];
  for (let d = startD; d >= endDaysAgo; d -= step) idxs.push(d);
  const n = idxs.length;
  const gapAt = n > 3 ? Math.floor(n * 0.4) : -1; // one visible missing observation
  // Accrual fraction based on absolute age over the full horizon (consistent across windows).
  const fracAt = (d) => Math.pow(Math.max(0, MAX_HISTORY_DAYS - d) / MAX_HISTORY_DAYS, 1.8);
  const jitter = 1 + (r() - 0.5) * 0.02;
  return idxs.map((d, i) => {
    const iso = new Date(Date.now() - d * 864e5).toISOString();
    const count = d === endDaysAgo ? (endDaysAgo === 0 ? total : Math.round(total * fracAt(d))) : Math.round(total * fracAt(d) * jitter);
    return { timestamp: iso, reviewCount: i === gapAt ? null : Math.max(0, count), source: "fixture", observedAt: iso };
  });
}

// Buy Box (New condition) statistics derived from offers + ownership model.
export function buildBuyBoxStats(offers = [], period = "90D", daysArg) {
  if (!offers.length) return null;
  const feat = offers.find((o) => o.featured) || offers[0];
  const own = buildFeaturedOwnership(offers, period === "24H" || period === "7D" || period === "30D" ? "30D" : period === "All" ? "1Y" : period);
  const winning = own.sellers.map((s) => s.avgLanded).filter(isValidNumber);
  const days = daysArg ?? periodToDays(period);
  const r = rngFrom(seedFrom(`bb|${period}`));
  const history = [];
  const step = days <= 7 ? 1 : days <= 90 ? 3 : 10;
  for (let d = days; d >= 0; d -= step) {
    const drift = (r() - 0.5) * 0.04;
    history.push({ t: new Date(Date.now() - d * 864e5).toISOString(), price: +(feat.delivered * (1 + drift)).toFixed(2), seller: feat.seller });
  }
  return {
    period, ownership: own,
    currentSeller: feat.seller, currentPrice: feat.delivered,
    itemPrice: feat.item_price, shippingPrice: feat.shipping, landedPrice: feat.delivered,
    fulfillment: feat.fulfillment, currentRating: feat.rating,
    avgWinningPrice: average(winning), minWinningPrice: minimum(winning), maxWinningPrice: maximum(winning),
    winCount: own.sellers[0]?.rotation ?? null,
    rotationCount: own.sellers.reduce((a, s) => a + (s.rotation || 0), 0),
    currentStreak: own.sellers.find((s) => s.featured)?.streak ?? "—",
    firstWin: new Date(Date.now() - days * 864e5).toISOString(),
    lastWin: feat.last_observed,
    history,
  };
}

// Used Buy Box — the current Walmart fixture source has only New-condition observations.
// Never substitute New values; report unavailability explicitly.
export function buildUsedBuyBoxStats(offers = []) {
  const used = offers.filter((o) => (o.condition || "New") !== "New");
  if (!used.length) return { available: false, reason: "Not provided by the current Walmart data source." };
  return { available: true, offers: used };
}
